import { useTranslation } from "react-i18next";
import {
    IconChevronRight,
    IconDeviceMobile,
    IconLoader2,
    IconMail,
    IconShieldCheck,
} from "@tabler/icons-react";

import { Text } from "@fragments";

import { CHANNELS } from "../../../hooks/useVerificationFlow";
import FlowMessage from "../FlowMessage";

const CHANNEL_ICONS = {
    [CHANNELS.EMAIL]: IconMail,
    [CHANNELS.SMS]: IconDeviceMobile,
};

function channelLabel(t, channel) {
    if (channel === CHANNELS.EMAIL) return t("verification.method.email");
    if (channel === CHANNELS.SMS) return t("verification.method.sms");
    return channel;
}

/**
 * Pick where the code goes: one row per eligible channel, plus a "verify this
 * phone" row when the account has a phone that is not confirmed yet.
 */
export default function MethodStep({
    methods,
    unverified,
    channel,
    message,
    sending,
    verifyingPhone,
    resendIn,
    onChoose,
    onVerifyPhone,
}) {
    const { t } = useTranslation("common");

    const busy = sending || verifyingPhone;
    const phones = unverified.filter((u) => u.channel === CHANNELS.SMS);
    const empty = methods.length === 0 && phones.length === 0;

    return (
        <div className="verification-method">
            {!empty && (
                <ul className="verification-method__list">
                    {methods.map((method) => {
                        const Icon = CHANNEL_ICONS[method.channel] || IconShieldCheck;
                        const active = sending && channel === method.channel;

                        return (
                            <li key={method.channel}>
                                <button
                                    type="button"
                                    className={`verification-method__row${active ? " is-busy" : ""}`}
                                    disabled={busy}
                                    aria-busy={active || undefined}
                                    onClick={() => onChoose(method.channel)}
                                >
                                    <span className="verification-method__icon">
                                        <Icon />
                                    </span>
                                    <span className="verification-method__copy">
                                        <Text.label contrast={9}>
                                            {channelLabel(t, method.channel)}
                                        </Text.label>
                                        <Text.span contrast={6}>{method.target}</Text.span>
                                    </span>
                                    <span className="verification-method__trail">
                                        {active ? (
                                            <IconLoader2 className="is-spinning" />
                                        ) : (
                                            <IconChevronRight />
                                        )}
                                    </span>
                                </button>
                            </li>
                        );
                    })}

                    {phones.map((phone) => (
                        <li key={`unverified-${phone.channel}`}>
                            <button
                                type="button"
                                className={`verification-method__row verification-method__row--unverified${verifyingPhone ? " is-busy" : ""}`}
                                disabled={busy}
                                aria-busy={verifyingPhone || undefined}
                                onClick={onVerifyPhone}
                            >
                                <span className="verification-method__icon">
                                    <IconDeviceMobile />
                                </span>
                                <span className="verification-method__copy">
                                    <Text.label contrast={9}>
                                        {t("verification.method.verifyPhone")}
                                    </Text.label>
                                    <Text.span contrast={6}>{phone.target}</Text.span>
                                    <Text.caption contrast={6}>
                                        {t("verification.method.phoneUnverified")}
                                    </Text.caption>
                                </span>
                                <span className="verification-method__trail">
                                    {verifyingPhone ? (
                                        <IconLoader2 className="is-spinning" />
                                    ) : (
                                        <IconChevronRight />
                                    )}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {empty && (
                <Text.p contrast={6} className="verification-method__empty">
                    {t("verification.method.empty")}
                </Text.p>
            )}

            <div className="verification-method__status" aria-live="polite">
                <FlowMessage message={message} resendIn={resendIn} />
            </div>
        </div>
    );
}
